import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionsByCategory } from '../data/questions';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const navigate = useNavigate();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const openModal = useCallback((categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCategory(null);
    setCurrentQuestionIndex(0);
  }, []);

  const getCurrentQuestions = useCallback(() => {
    if (!selectedCategory) return [];
    return questionsByCategory[selectedCategory] || [];
  }, [selectedCategory]);

  const setAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  }, []);

  const nextQuestion = useCallback(() => {
    const questions = getCurrentQuestions();
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      const params = new URLSearchParams();
      Object.entries(answers).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else if (value) {
          params.set(key, value);
        }
      });
      
      closeModal();
      navigate(`/advisors/${selectedCategory}?${params.toString()}`);
    }
  }, [currentQuestionIndex, getCurrentQuestions, answers, selectedCategory, navigate, closeModal]);

  const prevQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  const skipQuestion = useCallback(() => {
    nextQuestion();
  }, [nextQuestion]);

  const progress = getCurrentQuestions().length > 0 
    ? ((currentQuestionIndex + 1) / getCurrentQuestions().length) * 100 
    : 0;

  const value = {
    isModalOpen,
    selectedCategory,
    currentQuestionIndex,
    answers,
    progress,
    openModal,
    closeModal,
    getCurrentQuestions,
    setAnswer,
    nextQuestion,
    prevQuestion,
    skipQuestion,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export default SearchContext;
